import { loadDocumentTypeConfigs } from '@fv/models'
import { equipmentTypes } from '@fv/models/equipment'

import { DateFilter } from '@/components/CustomFilterForm/DateFilter'
import {
  directionOptions,
  pricingMethodOptions,
  pricingTypeOptions,
  shippingModeOptions,
  yesNoOptions,
} from '@/components/CustomFilterForm/filterOptions'
import { createLazyFilterComponent } from '@/components/CustomFilterForm/LazyMultiSelectFilter'
import {
  DestFilterComponent,
  OriginFilterComponent,
} from '@/components/CustomFilterForm/LocationFilter'
import type {
  FilterCategory,
  FilterConfig,
  FilterOptionsHook,
  FilterSelectOption,
} from '@/components/CustomFilterForm/types'
import {
  DEST_LOCATION_NAMES,
  ORIGIN_LOCATION_NAMES,
} from '@/utils/stringTransforms'

import { dateOptions, pastDateOptions } from '../date-range/types'
import {
  useAccountLocationFilterOptions,
  useAddressBookLocationFilterOptions,
  useCarriersFilterOptions,
  useGlCodesFilterOptions,
  useTagsFilterOptions,
  useUploadFilterOptions,
  useUsersFilterOptions,
  useVendorsFilterOptions,
} from './hooks/useLazyFilterOptions'

type FilterDefinition = {
  label: string
  category?: FilterCategory
  options?: FilterSelectOption[]
  useOptions?: FilterOptionsHook
  FilterComponent?: FilterConfig['FilterComponent']
  relatedNames?: readonly string[]
}

const shipperCarrierDocOptions = loadDocumentTypeConfigs
  .filter(c => c.allowedSources.some(s => s === 'shipper' || s === 'carrier'))
  .map(o => ({
    text: o.name,
    value: o.key,
  }))

export const filterRegistry = {
  pickupDate: {
    FilterComponent: DateFilter,
    category: 'date',
    label: 'Pickup date',
    options: dateOptions,
  },
  bookedDate: {
    FilterComponent: DateFilter,
    category: 'date',
    label: 'Booked date',
    options: pastDateOptions,
  },
  deliveryDate: {
    FilterComponent: DateFilter,
    category: 'date',
    label: 'Delivery date',
    options: dateOptions,
  },
  createdDate: {
    FilterComponent: DateFilter,
    category: 'date',
    label: 'Created date',
    options: pastDateOptions,
  },
  'direction[]': {
    label: 'Direction',
    options: directionOptions,
  },
  'mode[]': {
    label: 'Shipping mode',
    options: shippingModeOptions,
  },
  'equipmentType[]': {
    label: 'Equipment Type',
    options: equipmentTypes
      .filter(e => e.workflows.includes('truckload'))
      .sort((left, right) => left.name.localeCompare(right.name))
      .map(e => ({
        text: e.name,
        value: e.key,
      })),
  },
  isFreightCollect: {
    label: 'Bill Recipient / 3rd Party',
    options: yesNoOptions,
  },
  'pricingMethod[]': {
    label: 'Pricing Method',
    options: pricingMethodOptions,
  },
  isInterline: {
    label: 'Interline',
    options: yesNoOptions,
  },
  'pricingType[]': {
    label: 'Pricing Type',
    options: pricingTypeOptions,
  },
  isLiveLoad: {
    label: 'Live load',
    options: yesNoOptions,
  },
  isArchived: {
    label: 'Archived',
    options: yesNoOptions,
  },
  isSpot: {
    label: 'Spot Quote',
    options: yesNoOptions,
  },
  'hasDocs[]': {
    label: 'Has document',
    options: shipperCarrierDocOptions,
  },
  'missingDocs[]': {
    label: 'Missing document',
    options: shipperCarrierDocOptions,
  },
  'carrier[]': {
    label: 'Carrier',
    useOptions: useCarriersFilterOptions,
  },
  'quotedBy[]': {
    label: 'Quoted by',
    useOptions: useUsersFilterOptions,
  },
  'bookedBy[]': {
    label: 'Booked by',
    useOptions: useUsersFilterOptions,
  },
  'tag[]': {
    label: 'Tag',
    useOptions: useTagsFilterOptions,
  },
  'missingTag[]': {
    label: 'Missing tag',
    useOptions: useTagsFilterOptions,
  },
  'glCode[]': {
    label: 'GL Code',
    useOptions: useGlCodesFilterOptions,
  },
  'location[]': {
    label: 'Account location',
    useOptions: useAccountLocationFilterOptions,
  },
  'upload[]': {
    label: 'Upload batch',
    useOptions: useUploadFilterOptions,
  },
  'vendors[]': {
    label: 'Vendor',
    useOptions: useVendorsFilterOptions,
  },
  isVendor: {
    label: 'Vendor created',
    options: yesNoOptions,
  },
  [ORIGIN_LOCATION_NAMES[0]]: {
    FilterComponent: OriginFilterComponent,
    category: 'origin',
    label: 'Origin',
    useOptions: useAddressBookLocationFilterOptions,
    relatedNames: ORIGIN_LOCATION_NAMES.slice(1),
  },
  [DEST_LOCATION_NAMES[0]]: {
    FilterComponent: DestFilterComponent,
    category: 'dest',
    label: 'Destination',
    useOptions: useAddressBookLocationFilterOptions,
    relatedNames: DEST_LOCATION_NAMES.slice(1),
  },
} as const satisfies Record<string, FilterDefinition>

export type FilterRegistryKey = keyof typeof filterRegistry

function toFilterConfig(key: FilterRegistryKey): FilterConfig {
  const def = filterRegistry[key]

  const FilterComponent =
    'FilterComponent' in def
      ? def.FilterComponent
      : 'useOptions' in def
        ? createLazyFilterComponent(def.useOptions)
        : undefined

  return {
    name: key,
    label: def.label,
    ...('category' in def ? { category: def.category } : {}),
    ...('options' in def ? { options: def.options } : {}),
    ...('useOptions' in def ? { useOptions: def.useOptions } : {}),
    ...('relatedNames' in def
      ? { relatedNames: def.relatedNames ? [...def.relatedNames] : undefined }
      : {}),
    FilterComponent,
  }
}

export function getFilters(
  keys: readonly (FilterRegistryKey | FilterConfig)[],
): FilterConfig[] {
  return keys.map(key => (typeof key === 'string' ? toFilterConfig(key) : key))
}
